<template>
  <s-content id="brightspace-instructions">
    <p>{{ t('introduction') }}</p>
    <section id="register-tool">
      <h2>
        {{ t('step.1.heading') }}
      </h2>
      <ol>
        <li>
          <i18n-t keypath="step.1.1"></i18n-t>
        </li>
        <li>
          <i18n-t keypath="step.1.2">
            <template #adminTools>
              <strong>{{ t('brightspace.adminTools') }}</strong>
            </template>
          </i18n-t>
        </li>
        <li>
          <i18n-t keypath="step.1.3">
            <template #organizationRelated>
              <strong>{{ t('brightspace.organizationRelated') }}</strong>
            </template>
            <template #manageExtensibility>
              <strong>{{ t('brightspace.manageExtensibility') }}</strong>
            </template>
          </i18n-t>
        </li>
        <li>
          <i18n-t keypath="step.1.4">
            <template #ltiAdvantage>
              <strong>{{ t('brightspace.ltiAdvantage') }}</strong>
            </template>
          </i18n-t>
        </li>
        <li>
          <i18n-t keypath="step.1.5">
            <template #registerTool>
              <strong>{{ t('brightspace.registerTool') }}</strong>
            </template>
          </i18n-t>
          <ol>
            <li>
              <i18n-t keypath="step.1.5.1">
                <template #howToRegister>
                  <strong>{{ t('brightspace.howToRegister') }}</strong>
                </template>
                <template #dynamicRegistration>
                  <strong>{{ t('brightspace.dynamicRegistration') }}</strong>
                </template>
              </i18n-t>
            </li>
            <li>
              <i18n-t keypath="step.1.5.2">
                <template #endpoint>
                  <strong>{{ t('brightspace.endpoint') }}</strong>
                </template>
              </i18n-t>
              <copy-link
                class="mt-0.5"
                :href="
                  route('institutions.lti-registrations.create', {
                    institution: institutionId,
                    _query: {lms: 'brightspace'},
                  })
                "
              />
            </li>
            <li>
              <i18n-t keypath="step.1.5.3">
                <template #configureDeployment>
                  <strong>{{ t('brightspace.configureDeployment') }}</strong>
                </template>
              </i18n-t>
            </li>
            <li>
              <i18n-t keypath="step.1.5.4">
                <template #register>
                  <strong>{{ t('brightspace.register') }}</strong>
                </template>
              </i18n-t>
            </li>
          </ol>
        </li>

        <li>
          <i18n-t keypath="step.1.6">
            <template #stemble>
              <strong>{{ t('stemble') }}</strong>
            </template>
            <template #create>
              <strong>{{ t('stemble.createRegistration') }}</strong>
            </template>
            <template #ltiAdvantage>
              <strong>{{ t('brightspace.ltiAdvantage') }}</strong>
            </template>
          </i18n-t>
        </li>
      </ol>
    </section>
    <section id="enable-tool">
      <h2>{{ t('step.2.heading') }}</h2>
      <ol>
        <li>
          <i18n-t keypath="step.2.1">
            <template #brightspace>
              <strong>{{ t('brightspace') }}</strong>
            </template>
            <template #stemble>
              <strong>{{ t('stemble') }}</strong>
            </template>
          </i18n-t>
        </li>

        <li>
          <i18n-t keypath="step.2.2">
            <template #enable>
              <strong>{{ t('brightspace.enable') }}</strong>
            </template>
          </i18n-t>
        </li>
        <li>
          <i18n-t keypath="step.2.3">
            <template #roles>
              <strong>{{ t('brightspace.roles') }}</strong>
            </template>
            <template #sendInstitutionRoles>
              <strong>{{ t('brightspace.sendInstitutionRoles') }}</strong>
            </template>
          </i18n-t>
        </li>
        <li>
          <i18n-t keypath="step.save">
            <template #save>
              <strong>{{ t('brightspace.save') }}</strong>
            </template>
          </i18n-t>
        </li>
      </ol>
    </section>
    <section id="enable-deployment">
      <h2>
        {{ t('step.3.heading') }}
      </h2>
      <ol>
        <li>
          <i18n-t keypath="step.3.1">
            <template #viewDeployments>
              <strong>{{ t('brightspace.viewDeployments') }}</strong>
            </template>
          </i18n-t>
        </li>
        <li>
          <i18n-t keypath="step.3.2">
            <template #stemble>
              <strong>{{ t('stemble') }}</strong>
            </template>
          </i18n-t>
        </li>
        <li>
          <i18n-t keypath="step.3.3">
            <template #enable>
              <strong>{{ t('brightspace.enable') }}</strong>
            </template>
          </i18n-t>
        </li>
        <li>
          <i18n-t keypath="step.3.4">
            <template #securitySettings>
              <strong>{{ t('brightspace.securitySettings') }}</strong>
            </template>
          </i18n-t>
          <ul>
            <li>{{ t('brightspace.orgUnitInformation') }}</li>
            <li>{{ t('brightspace.userInformation') }}</li>
            <li>{{ t('brightspace.linkInformation') }}</li>
            <li>{{ t('brightspace.classList') }}</li>
          </ul>
        </li>
        <li>
          <i18n-t keypath="step.3.5">
            <template #configurationSettings>
              <strong>{{ t('brightspace.configurationSettings') }}</strong>
            </template>
            <template #openAsExternalResource>
              <strong>{{ t('brightspace.openAsExternalResource') }}</strong>
            </template>
          </i18n-t>
        </li>
        <li>
          <i18n-t keypath="step.save">
            <template #save>
              <strong>{{ t('brightspace.save') }}</strong>
            </template>
          </i18n-t>
        </li>
      </ol>
    </section>
    <section id="make-available">
      <h2>
        {{ t('step.4.heading') }}
      </h2>

      <ol>
        <li>
          <i18n-t keypath="step.4.1"></i18n-t>
        </li>
        <li>
          <i18n-t keypath="step.4.2">
            <template #addOrgUnits>
              <strong>{{ t('brightspace.addOrgUnits') }}</strong>
            </template>
          </i18n-t>
        </li>
        <li>
          <i18n-t keypath="step.4.3">
            <template #orgUnitType>
              <strong>{{ t('brightspace.orgUnitType') }}</strong>
            </template>
            <template #organization>
              <strong>{{ t('brightspace.organization') }}</strong>
            </template>
          </i18n-t>
        </li>
        <li>
          <i18n-t keypath="step.4.4"></i18n-t>
        </li>
        <li>
          <i18n-t keypath="step.4.5">
            <template #allDescendents>
              <strong>{{ t('brightspace.allDescendents') }}</strong>
            </template>
          </i18n-t>
        </li>
        <li>
          <i18n-t keypath="step.4.6">
            <template #add>
              <strong>{{ t('brightspace.add') }}</strong>
            </template>
          </i18n-t>
        </li>
        <li>
          <i18n-t keypath="step.save">
            <template #save>
              <strong>{{ t('brightspace.save') }}</strong>
            </template>
          </i18n-t>
        </li>
      </ol>
    </section>
    <section id="create-launch-link">
      <h2>
        {{ t('step.5.heading') }}
      </h2>

      <ol>
        <li>
          <i18n-t keypath="step.5.1"></i18n-t>
        </li>
        <li>
          <i18n-t keypath="step.5.2">
            <template #viewLinks>
              <strong>{{ t('brightspace.viewLinks') }}</strong>
            </template>
          </i18n-t>
        </li>
        <li>
          <i18n-t keypath="step.5.3">
            <template #newLink>
              <strong>{{ t('brightspace.newLink') }}</strong>
            </template>
          </i18n-t>
        </li>
        <li>
          <i18n-t keypath="step.5.4">
            <template #linkName>
              <strong>{{ t('stemble.launch') }}</strong>
            </template>
          </i18n-t>
        </li>
        <li>
          <i18n-t keypath="step.5.5"></i18n-t>
          <copy-link class="mt-0.5" :href="route('api.v1.ltia.launch')" />
        </li>
        <li>
          <i18n-t keypath="step.save">
            <template #save>
              <strong>{{ t('brightspace.save') }}</strong>
            </template>
          </i18n-t>
        </li>
      </ol>
    </section>
  </s-content>
</template>
<script setup lang="ts">
import {useI18n} from 'vue-i18n';
import CopyLink from './CopyLink.vue';
import {route} from 'ziggy-js';
import SContent from '../../../design-system/SContent.vue';

defineProps<{
  institutionId: number;
}>();

const {t} = useI18n({
  useScope: 'local',
  inheritLocale: true,
});
</script>
<i18n>
{
  "en": {
    "introduction": "To register an LTI platform in Brightspace, follow these steps:",
    "step.save": "Click on {save}.",
    "step.1.heading": "Register Stemble Tool",
    "step.1.1": "Log in to your Brightspace account.",
    "step.1.2": "Open the {adminTools} menu in the top right corner of the navigation bar.",
    "step.1.3": "Under {organizationRelated}, select {manageExtensibility}.",
    "step.1.4": "Switch to the {ltiAdvantage} tab.",
    "step.1.5": "Click on {registerTool}.",
    "step.1.5.1": "Under {howToRegister} select {dynamicRegistration}.",
    "step.1.5.2": "Copy the URL below and paste it into the {endpoint} field:",
    "step.1.5.3": "Check the box for {configureDeployment}.",
    "step.1.5.4": "Click on {register} and you will be redirected to Stemble to complete the registration.",
    "step.1.6": "In {stemble}, click on {create} to create the registration and be redirected back to the Brightspace {ltiAdvantage} tab.",
    "step.2.heading": "Enable Stemble Tool",
    "step.2.1": "In {brightspace}, click on the new {stemble} tool.",
    "step.2.2": "Check the box to {enable} the tool.",
    "step.2.3": "Under {roles}, check the box for {sendInstitutionRoles}.",
    "step.3.heading": "Enabling the Stemble Deployment",
    "step.3.1": "Scroll back to the bottom of the Stemble Tool page and click the {viewDeployments} link.",
    "step.3.2": "Click on the {stemble} deployment to view the details.",
    "step.3.3": "Make sure to {enable} the tool.",
    "step.3.4": "Under {securitySettings} check the boxes for:",
    "step.3.5": "Under {configurationSettings} check the box for {openAsExternalResource}.",
    "step.4.heading": "Make Stemble Deployment Available",
    "step.4.1": "Scroll back to the bottom of the Deployment page",
    "step.4.2": "Click on the {addOrgUnits} button to open the dialog.",
    "step.4.3": "Click on the {orgUnitType} filter and select {organization}.",
    "step.4.4": "Check the box on the left column for your organization.",
    "step.4.5": "Select the {allDescendents} option in the right-most column.",
    "step.4.6": "Click the {add} button at the bottom of the dialog.",
    "step.5.heading": "Create the Stemble Launch Link",
    "step.5.1": "Scroll back to the bottom of the Deployment page",
    "step.5.2": "Click on the {viewLinks} link.",
    "step.5.3": "Click on the {newLink} button.",
    "step.5.4": "Set the name to {linkName}",
    "step.5.5": "Set the URL to:",
    "brightspace": "Brightspace",
    "brightspace.adminTools": "Admin Tools",
    "brightspace.organizationRelated": "Organization Related",
    "brightspace.manageExtensibility": "Manage Extensibility",
    "brightspace.ltiAdvantage": "LTI Advantage",
    "brightspace.registerTool": "Register Tool",
    "brightspace.howToRegister": "How would you like to register your tool?",
    "brightspace.dynamicRegistration": "Dynamic Registration",
    "brightspace.endpoint": "Tool initiation registration endpoint",
    "brightspace.configureDeployment": "Configure Deployment",
    "brightspace.register": "Register",
    "brightspace.enable": "Enable",
    "brightspace.roles": "Roles",
    "brightspace.sendInstitutionRoles": "Send Institution Roles",
    "brightspace.save": "Save",
    "brightspace.viewDeployments": "View Deployments",
    "brightspace.securitySettings": "Security Settings",
    "brightspace.orgUnitInformation": "Organization Unit Information",
    "brightspace.userInformation": "User Information",
    "brightspace.linkInformation": "Link Information",
    "brightspace.classList": "Classlist including users not known to this deployment",
    "brightspace.configurationSettings": "Configuration Settings",
    "brightspace.openAsExternalResource": "Open as External Resource",
    "brightspace.addOrgUnits": "Add Org Units",
    "brightspace.orgUnitType": "Org Unit Type",
    "brightspace.organization": "Organization",
    "brightspace.allDescendents": "This org unit and all descendants",
    "brightspace.add": "Add",
    "brightspace.viewLinks": "View Links",
    "brightspace.newLink": "New Link",
    "stemble": "Stemble",
    "stemble.createRegistration": "Create LTI Registration",
    "stemble.launch": "Stemble Launch"
  },
  "fr": {
    "introduction": "Pour enregistrer une plateforme LTI dans Brightspace, suivez ces étapes :",
    "step.save": "Cliquez sur {save}.",
    "step.1.heading": "Enregistrer l'outil Stemble",
    "step.1.1": "Connectez-vous à votre compte Brightspace.",
    "step.1.2": "Ouvrez le menu {adminTools} dans le coin supérieur droit de la barre de navigation.",
    "step.1.3": "Sous {organizationRelated}, sélectionnez {manageExtensibility}.",
    "step.1.4": "Passez à l’onglet {ltiAdvantage}.",
    "step.1.5": "Cliquez sur {registerTool}.",
    "step.1.5.1": "Sous {howToRegister}, sélectionnez {dynamicRegistration}.",
    "step.1.5.2": "Copiez l’URL ci-dessous et collez-la dans le champ {endpoint} :",
    "step.1.5.3": "Cochez la case {configureDeployment}.",
    "step.1.5.4": "Cliquez sur {register} et vous serez redirigé vers Stemble pour terminer l'enregistrement.",
    "step.1.6": "Dans {stemble}, cliquez sur {create} pour créer l'enregistrement et être redirigé vers l'onglet {ltiAdvantage} de Brightspace.",
    "step.2.heading": "Activer l'outil Stemble",
    "step.2.1": "Dans {brightspace}, cliquez sur le nouvel outil {stemble}.",
    "step.2.2": "Cochez la case pour {enable} l’outil.",
    "step.2.3": "Sous {roles}, cochez la case pour {sendInstitutionRoles}.",
    "step.3.heading": "Activer le déploiement Stemble",
    "step.3.1": "Faites défiler vers le bas de la page de l'outil Stemble et cliquez sur le lien {viewDeployments}.",
    "step.3.2": "Cliquez sur le déploiement {stemble} pour voir les détails.",
    "step.3.3": "Assurez-vous de {enable} l’outil.",
    "step.3.4": "Sous {securitySettings}, cochez les cases suivantes :",
    "step.3.5": "Sous {configurationSettings}, cochez la case {openAsExternalResource}.",
    "step.4.heading": "Rendre le déploiement Stemble disponible",
    "step.4.1": "Faites défiler à nouveau jusqu’en bas de la page de déploiement",
    "step.4.2": "Cliquez sur le bouton {addOrgUnits} pour ouvrir la boîte de dialogue.",
    "step.4.3": "Cliquez sur le filtre {orgUnitType} et sélectionnez {organization}.",
    "step.4.4": "Cochez la case dans la colonne de gauche pour votre organisation.",
    "step.4.5": "Sélectionnez l’option {allDescendents} dans la colonne la plus à droite.",
    "step.4.6": "Cliquez sur le bouton {add} en bas de la boîte de dialogue.",
    "step.5.heading": "Créer le lien de lancement Stemble",
    "step.5.1": "Faites défiler à nouveau jusqu’en bas de la page de déploiement",
    "step.5.2": "Cliquez sur le lien {viewLinks}.",
    "step.5.3": "Cliquez sur le bouton {newLink}.",
    "step.5.4": "Définissez le nom sur {linkName}",
    "step.5.5": "Définissez l’URL sur :",
    "brightspace": "Brightspace",
    "brightspace.adminTools": "Outils d’administration",
    "brightspace.organizationRelated": "Organisation",
    "brightspace.manageExtensibility": "Gérer l’extensibilité",
    "brightspace.ltiAdvantage": "LTI Advantage",
    "brightspace.registerTool": "Enregistrer un outil",
    "brightspace.howToRegister": "Comment souhaitez-vous enregistrer votre outil ?",
    "brightspace.dynamicRegistration": "Enregistrement dynamique",
    "brightspace.endpoint": "Point de terminaison d’enregistrement d’initialisation de l’outil",
    "brightspace.configureDeployment": "Configurer le déploiement",
    "brightspace.register": "Enregistrer",
    "brightspace.enable": "Activer",
    "brightspace.roles": "Rôles",
    "brightspace.sendInstitutionRoles": "Envoyer les rôles de l’institution",
    "brightspace.save": "Enregistrer",
    "brightspace.viewDeployments": "Voir les déploiements",
    "brightspace.securitySettings": "Paramètres de sécurité",
    "brightspace.orgUnitInformation": "Informations sur l’unité organisationnelle",
    "brightspace.userInformation": "Informations sur l’utilisateur",
    "brightspace.linkInformation": "Informations sur le lien",
    "brightspace.classList": "Liste de classe incluant les utilisateurs inconnus de ce déploiement",
    "brightspace.configurationSettings": "Paramètres de configuration",
    "brightspace.openAsExternalResource": "Ouvrir comme ressource externe",
    "brightspace.addOrgUnits": "Ajouter des unités organisationnelles",
    "brightspace.orgUnitType": "Type d’unité organisationnelle",
    "brightspace.organization": "Organisation",
    "brightspace.allDescendents": "Cette unité organisationnelle et tous ses descendants",
    "brightspace.add": "Ajouter",
    "brightspace.viewLinks": "Voir les liens",
    "brightspace.newLink": "Nouveau lien",
    "stemble": "Stemble",
    "stemble.createRegistration": "Créer un enregistrement LTI",
    "stemble.launch": "Lancement Stemble"
  }
}
</i18n>
